import { getAuthToken, redirectToIdentityProvider } from 'utils/auth.util';
import { getAbortErrorData, getErrorMessage, sendErrorToNewRelic } from 'utils/error.util';
import ApiAbortController from './ApiAbortController';

const envVar = name => {
  if (!window.__env__[name]) {
    throw new Error(`${name} environment variable not found!`);
  }

  return window.__env__[name];
};

const processError = (error, apiUrl, params, href, fetchType, apiErrorMessage) => {
  const newRelicCustomData = { error, apiUrl, params, href, fetchType, apiErrorMessage };
  if (error.name === 'AbortError') {
    const errorData = getAbortErrorData({ fetchType, params });
    sendErrorToNewRelic(new Error('AbortError'), newRelicCustomData);
    return Promise.reject(errorData);
  }

  if (error.message === 'Failed to fetch') {
    const errorData = {
      message: "Looks like we're having a problem.",
      remedy: 'Please try again in a couple of minutes.',
    };
    sendErrorToNewRelic(new Error('Failed to fetch'), newRelicCustomData);
    return Promise.reject(errorData);
  }

  const { status } = error;

  const errorData = getErrorMessage({
    status,
    apiErrorMessage,
    fetchType,
  });

  sendErrorToNewRelic(new Error('Unknown api error'), { ...newRelicCustomData, status });
  return Promise.reject(errorData);
};

export const getBaseUrl = () => envVar('GATEWAY_URL');

export const callApiWithTimeout = (
  apiUrl,

  params = {},
  fetchType = '',
  maxTime = 15000,
  jsonRequest = true,
  controller = new ApiAbortController(),
) => {
  const { href } = window.location;
  const { signal } = controller.controller;

  const token = getAuthToken();

  if (!token) {
    redirectToIdentityProvider();
    return Promise.reject(new Error('No token specified!'));
  }

  setTimeout(() => {
    controller.abort('Timeout');
  }, maxTime);

  const contentTypeHeader = jsonRequest ? { 'Content-type': 'application/json' } : {};

  return fetch(apiUrl, {
    ...params,
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      'User-Agent': 'digital-merch-ui',
      ...contentTypeHeader,
    },
  })
    .then(res => {
      if (res.status === 204) return '';
      if (res.ok) {
        return res.json();
      }
      throw res;
    })
    .catch(error => {
      return error.text
        ? error.text().then(textResponse => {
            const { message } = textResponse ? parseIfJson(textResponse) : {};
            return processError(error, apiUrl, params, href, fetchType, message);
          })
        : processError(error, apiUrl, params, href, fetchType);
    });
};

const filenameFromHeader = response =>
  response.headers
    .get('content-disposition')
    .split(';')
    .find(n => n.includes('filename='))
    .replace('filename=', '')
    .trim();

export const callApiWithTimeoutWithDownload = (
  apiUrl,

  params = {},
  fetchType,
  maxTime = 20000,
  controller = new ApiAbortController(),
) => {
  const { href } = window.location;
  const { signal } = controller;

  const token = getAuthToken();

  if (!token) {
    redirectToIdentityProvider();
    return Promise.reject(new Error('No token specified!'));
  }

  setTimeout(() => {
    controller.abort();
  }, maxTime);

  return fetch(apiUrl, {
    method: 'POST',
    ...params,
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      'User-Agent': 'digital-merch-ui',
    },
  })
    .then(async response => {
      if (response.ok)
        return {
          blob: await response.blob(),
          filename: filenameFromHeader(response),
        };

      throw response;
    })
    .catch(error => {
      return error.text
        ? error.text().then(textResponse => {
            const { message } = textResponse ? parseIfJson(textResponse) : {};
            return processError(error, apiUrl, params, href, fetchType, message);
          })
        : processError(error, apiUrl, params, href, fetchType);
    });
};

export const callApi = apiCallDetails => {
  const { apiUrl, params, fetchType, maxTime, jsonRequest, controller, download } = apiCallDetails;
  if (download) {
    return callApiWithTimeoutWithDownload(apiUrl, params, fetchType, maxTime, controller);
  }
  return callApiWithTimeout(apiUrl, params, fetchType, maxTime, jsonRequest, controller);
};

function parseIfJson(str) {
  try {
    return JSON.parse(str);
  } catch {
    return {};
  }
}
